import { useState } from 'react';
import { List, CaretDown } from '@phosphor-icons/react';
import type { TopicSection } from '@researcher/shared';

interface TopicTableOfContentsProps {
  sections: TopicSection[];
  activeSection?: string;
}

export default function TopicTableOfContents({ sections, activeSection }: TopicTableOfContentsProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const scrollTo = (sectionType: string) => {
    const el = document.getElementById(`section-${sectionType}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileOpen(false);
    }
  };

  // Mobile dropdown
  const mobileContent = (
    <div className="lg:hidden mb-4">
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="flex items-center gap-2 w-full px-3 py-2 bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300"
      >
        <List size={16} />
        <span>Table of Contents</span>
        <CaretDown size={14} className={`ml-auto transition-transform ${mobileOpen ? 'rotate-180' : ''}`} />
      </button>
      {mobileOpen && (
        <div className="mt-1 bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-2">
          {sections.map(s => (
            <button
              key={s.type}
              onClick={() => scrollTo(s.type)}
              className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-pink-600 dark:hover:text-pink-400 rounded"
            >
              {s.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Desktop sidebar
  const desktopContent = (
    <div className="hidden lg:block">
      <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
        In this article
      </h4>
      <nav className="space-y-1">
        {sections.map(s => (
          <button
            key={s.type}
            onClick={() => scrollTo(s.type)}
            className={`block w-full text-left px-3 py-1.5 text-sm rounded transition-colors ${
              activeSection === s.type
                ? 'text-pink-600 dark:text-pink-400 bg-pink-50 dark:bg-pink-950 font-medium'
                : 'text-gray-600 dark:text-gray-400 hover:text-pink-600 dark:hover:text-pink-400'
            }`}
          >
            {s.title}
          </button>
        ))}
      </nav>
    </div>
  );

  return (
    <>
      {mobileContent}
      {desktopContent}
    </>
  );
}
